import { useDispatch, useSelector } from 'react-redux'

import DatepickerWithLabel from '../../../../common/interactive/DatepickerWithLabel'
import InputBox from '../../../../common/interactive/InputBox'
import { caseActions } from '../../../../../store/case'
import { caseQuickUpdate } from '../../../../../lib/fetchApi'
import { notificationActions } from '../../../../../store/notifications'
import CheckboxCell from '../../../../common/interactive/CheckboxCell'
import { atpProvidersOptions, frontendOptions } from '../../../../../lib/systemData'
import { useMemo } from 'react'

import classes from '../SurgicalDrawer.module.css'
import CustomSelect from '../../../../common/interactive/CustomSelect'
import { getSurgeryRecommendedStatusValue, searchTabInCaseType } from '../../../../../lib/misc'
import GlobalTooltip from '../../../../common/GlobalTooltip'

export default function SurgeryEvaluation({itemData, apiCase, isPreauthNeededSaving = false}) {
    const dispatch = useDispatch()

    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase) 
    const normalizedProviderId =
        itemData?.step_4_eval_provider_id ??
        itemData?.step_4_eval_provider?.id ??
        itemData?.step_4_eval_provider
    const numericProviderId = Number(normalizedProviderId)
    const hasNumericProviderId = !Number.isNaN(numericProviderId) && numericProviderId > 0
    const majorMeds = activeCase?.major_meds ?? []
    const hasPrimaryMajorMed = majorMeds.find((item) => item.is_primary_insurance)
    const showPreauth =
        searchTabInCaseType(activeCase, 'workers_comp') ||
        (!!itemData.step_1_preauthorization_required && !isPreauthNeededSaving)
    const showDeferralReason = itemData.step_4_status === 3
    const showInsuranceAuthorization =
        searchTabInCaseType(activeCase, "worker_comp") ||
        (searchTabInCaseType(activeCase, "major_med") && hasPrimaryMajorMed)
    const surgeryRecommendationOptions = useMemo(() => frontendOptions()?.surgeryRecomandation || [], [])
    const procedureSuggestions = useMemo(() => frontendOptions()?.surgeryProcedureTypes || [], [])
    const recommendedStatusValue = useMemo(() => getSurgeryRecommendedStatusValue(), [])
    const showProcedureType = useMemo(() => {
        const s = itemData.step_4_status
        return s != null && s !== '' && Number(s) === Number(recommendedStatusValue)
    }, [itemData.step_4_status, recommendedStatusValue])
    const deferralReasonOptions = useMemo(() => frontendOptions()?.reasonForDefferal || [], [])
    const isInsuranceAuthorizationDisabled = itemData.step_4_status || (itemData.step_4_status && itemData.step_4_status !== 4)
   
    const selectedEvalProvider = atpProvidersOptions()?.find((provider) => (
       
        Number(provider?.id) === numericProviderId || Number(provider?.value) === numericProviderId
    ))
    
   
    const evalProviderLabel =
    `${selectedEvalProvider?.first_name || ''} ${selectedEvalProvider?.last_name || ''}`.trim() ||
        (hasNumericProviderId ? '' : (itemData?.step_4_eval_provider || '')) ||
        ''

    const makeIdentifier = (field) => ({
        folder: 'surgeries',
        type: apiCase,
        field,
        type_id: itemData.id
    })

    const updateHandler = async (data) => {
        if (
            data.identifier.field === 'step_4_different_performing_surgeon'
            && Number(itemData.step_4_status) === Number(recommendedStatusValue)
            && !data.data.value
        ) {
            return
        }

        const reqBody = {
            type: data.identifier.type,
            type_id: data.identifier.type_id,
            fields: {
                [data.identifier.field] : data.data.value,
            }
        }

        const dispatchBody = {
            folder: data.identifier.folder,
            item: {
                itemId: itemData.id,
                fields: [
                    {
                        field: data.identifier.field,
                        value: data.data.value
                    }
                ]              
            },
        }

        if (data.identifier.field === 'step_4_status') {

            reqBody.fields.step_4_surgery_insurance_authorization_obtained = 0
            dispatchBody.item.fields.push({
                field: 'step_4_surgery_insurance_authorization_obtained',
                value: 0
            })
            const nextStatus = data.data.value
            if (nextStatus == null || nextStatus === '') {
                reqBody.fields.step_4_procedure_type = ''
                reqBody.fields.step_4_reason_for_defferal = ''
                dispatchBody.item.fields.push({
                    field: 'step_4_procedure_type',
                    value: ''
                })
                dispatchBody.item.fields.push({
                    field: 'step_4_reason_for_defferal',
                    value: ''
                })
            } else if (Number(nextStatus) !== Number(recommendedStatusValue)) {
                reqBody.fields.step_4_procedure_type = ''
                dispatchBody.item.fields.push({
                    field: 'step_4_procedure_type',
                    value: ''
                })
            } else {
                reqBody.fields.step_4_different_performing_surgeon = 1
                dispatchBody.item.fields.push({
                    field: 'step_4_different_performing_surgeon',
                    value: 1
                })
            }
        }

        dispatch(caseActions.modifyActiveCaseMultiple(dispatchBody))

        

        const update = await caseQuickUpdate(userToken, activeCase.case.id, reqBody)

        if (update && update.errorMessage) {
            dispatch(notificationActions.setError(update))
        } else {            
        }        
        
    }
    return <>
        <h3 className={classes.pannel_title}>#4 Surgery Evaluation</h3>
        {showPreauth ? <div className={classes.drawer_row}>
            <CheckboxCell label={'Preauthorization Obtained'} action={updateHandler} checked={itemData.step_4_preauthorization_obtained} direction={'reverse'} identifier={makeIdentifier('step_4_preauthorization_obtained')}/>
        </div> : null}
        <div className={classes.drawer_row}>
            <InputBox label={'Eval. Provider'} action={updateHandler} currentValue={evalProviderLabel} identifier={makeIdentifier('step_4_eval_provider')}/>
        </div>
        <div className={classes.drawer_row}>
            <div className={`${classes.drawer_flex_block} ${classes.drawer_flex_block_equal}`}>
                <DatepickerWithLabel label={'Eval Appt Date'} action={updateHandler} defaultDate={itemData.step_4_eval_appt_date} identifier={makeIdentifier('step_4_eval_appt_date')}/>
                <div className='flex-container'>
                    <DatepickerWithLabel label={'Est Surgical Date'} action={updateHandler} defaultDate={itemData.step_4_est_surgical_date} identifier={makeIdentifier('step_4_est_surgical_date')} disabled={!itemData.step_2_total_funds_approved}/>
                    {!itemData.step_2_total_funds_approved ? <GlobalTooltip noMargin={true}>
                        Final Financial Approved not obtained
                    </GlobalTooltip> : null}
                </div>
            </div>            
        </div>

        <div className={classes.drawer_row}>
            <CustomSelect label={'Surgical Status'} optionsData={surgeryRecommendationOptions} preSelectedValue={itemData.step_4_status} action={updateHandler} identifier={makeIdentifier('step_4_status')} hasRemove={true}/>
        </div>

        {showProcedureType ? <div className={classes.drawer_row}>
            <InputBox label={'Procedure Type'} action={updateHandler} currentValue={itemData.step_4_procedure_type || ''} identifier={makeIdentifier('step_4_procedure_type')} suggestions={procedureSuggestions}/>
        </div> : null}

        {showDeferralReason && <div className={classes.drawer_row}>
            <CustomSelect label={'Reason for deferral'} optionsData={deferralReasonOptions} preSelectedValue={itemData.step_4_reason_for_defferal} action={updateHandler} identifier={makeIdentifier('step_4_reason_for_defferal')}/>
        </div> }   

        <div className={classes.drawer_row}>
            <CheckboxCell label={'Different performing surgeon'} action={updateHandler} checked={itemData.step_4_different_performing_surgeon} direction={'reverse'} identifier={makeIdentifier('step_4_different_performing_surgeon')} isDisabled={showProcedureType}/>
        </div>


        {showInsuranceAuthorization && <>
            <div className={classes.drawer_row}>
                <div className='flex-container'>
                    <CheckboxCell label={'Sugery Insurance Authorization Obtained'} action={updateHandler} checked={itemData.step_4_surgery_insurance_authorization_obtained} direction={'reverse'} identifier={makeIdentifier('step_4_surgery_insurance_authorization_obtained')} isDisabled={isInsuranceAuthorizationDisabled}/>
                    <GlobalTooltip>
                        Only required for WC or for any other Case Type with MM available, whenever Surgery Approved.
                    </GlobalTooltip>
                </div>
            </div>
            
        </>}

    </>
}